import { useMemo } from "react";
import { ChevronDown, Check } from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuCheckboxItem,
	DropdownMenuItem,
} from "../../../components/ui/dropdown-menu";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";

interface PriceList {
	id: string;
	name: string;
	tp_value: number;
	is_deactivated?: boolean | null;
}

interface PriceListDropdownProps {
	priceLists: PriceList[];
	visiblePriceListIds: Set<string>;
	onTogglePriceList: (priceListId: string) => void;
	onSetVisiblePriceLists: (priceListIds: string[]) => void;
	maxSelections?: number;
}

export const PriceListDropdown: React.FC<PriceListDropdownProps> = ({
	priceLists,
	visiblePriceListIds,
	onTogglePriceList,
	onSetVisiblePriceLists,
	maxSelections = 5,
}) => {
	const selectedCount = visiblePriceListIds.size;
	
	const canSelectMore = selectedCount < maxSelections;
	
	const sortedPriceLists = useMemo(() => {
		return [...priceLists]
			.filter(pl => pl.is_deactivated !== true)
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [priceLists]);

	const handleSelectAll = () => {
		const idsToSelect = sortedPriceLists.slice(0, maxSelections).map(pl => pl.id);
		onSetVisiblePriceLists(idsToSelect);
	};

	const handleClearAll = () => {
		onSetVisiblePriceLists([]);
	};

	const handleToggle = (priceListId: string) => {
		const isSelected = visiblePriceListIds.has(priceListId);
		
		// If trying to select and already at max, don't allow
		if (!isSelected && !canSelectMore) {
			return;
		}
		
		// If trying to deselect and it's the last one, don't allow
		if (isSelected && selectedCount === 1) {
			return;
		}
		
		onTogglePriceList(priceListId);
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="outline" size="sm" className="gap-2">
					<span>Preislisten</span>
					{selectedCount > 0 && (
						<span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
							{selectedCount}
						</span>
					)}
					<ChevronDown className="h-3.5 w-3.5" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-72">
				<DropdownMenuLabel className="flex justify-between items-center">
					<span>Sichtbare Preislisten</span>
					<span className="text-xs font-normal text-gray-500">
						Max. {maxSelections} auswählbar
					</span>
				</DropdownMenuLabel>
				<DropdownMenuSeparator />
				
				<div className="flex gap-1 px-2 py-1.5">
					<Button
						variant="ghost"
						size="sm"
						className="flex-1 h-7 text-xs"
						onClick={(e) => {
							e.preventDefault();
							handleSelectAll();
						}}
						disabled={priceLists.length === 0}
					>
						Alle auswählen
					</Button>
					<Button
						variant="ghost"
						size="sm"
						className="flex-1 h-7 text-xs"
						onClick={(e) => {
							e.preventDefault();
							handleClearAll();
						}}
						disabled={selectedCount === 0}
					>
						Alle abwählen
					</Button>
				</div>
				
				<DropdownMenuSeparator />
				
				<div className="max-h-80 overflow-y-auto">
					{sortedPriceLists.length === 0 ? (
						<div className="px-2 py-4 text-sm text-gray-500 text-center">
							Keine Preislisten verfügbar
						</div>
					) : (
						sortedPriceLists.map((priceList) => {
							const isSelected = visiblePriceListIds.has(priceList.id);
							const isDisabled = !isSelected && !canSelectMore;
							const isLastSelected = isSelected && selectedCount === 1;
							
							return (
								<DropdownMenuCheckboxItem
									key={priceList.id}
									checked={isSelected}
									disabled={isDisabled || isLastSelected}
									onCheckedChange={() => handleToggle(priceList.id)}
									className={cn(
										"flex items-center justify-between py-2",
										isDisabled && "opacity-50 cursor-not-allowed"
									)}
								>
									<div className="flex-1 min-w-0">
										<div className="font-medium text-sm">{priceList.name}</div>
										<div className="text-xs text-gray-500">
											TP: {priceList.tp_value}
										</div>
									</div>
								</DropdownMenuCheckboxItem>
							);
						})
					)}
				</div>
				
				{selectedCount === maxSelections && (
					<>
						<DropdownMenuSeparator />
						<div className="px-2 py-2 text-xs text-amber-600 text-center">
							Maximale Anzahl erreicht
						</div>
					</>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};